import React, { useState, useEffect, useContext } from 'react';
import { createPortal } from 'react-dom';
import axiosInstance from '../../../config/axios';
import toast from 'react-hot-toast';
import { AppContext } from '../../../contexts/AppContext';
import { assets } from '../../../assets/assets';
import { getLogoUrl } from '../../../utils/imageUtils';

const TaskDetailModal = ({ isOpen, task, onClose, isManager, onTaskUpdate, isEditMode = false }) => {
  const { userData } = useContext(AppContext);
  const [status, setStatus] = useState(task?.statut || 'todo');
  const [processing, setProcessing] = useState(false);
  const [fullTask, setFullTask] = useState(task);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (task) {
      setStatus(task.statut || 'todo');
      setFullTask(task);
      // Charger les détails complets de la tâche si elle n'a pas les assignments
      if (isOpen && task.id && (!task.assignments || task.assignments.length === 0)) {
        loadFullTask();
      }
    }
  }, [task, isOpen]);

  const loadFullTask = async () => {
    if (!task?.id) return;
    
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/tasks/${task.id}`);
      if (response.data.success) {
        setFullTask(response.data.data);
      }
    } catch (error) {
      console.error('Erreur chargement détails tâche:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !task) return null;

  const currentTask = fullTask || task;

  const handleStatusChange = async (newStatus) => {
    if (processing || status === newStatus) return;
    
    try {
      setProcessing(true);
      const response = await axiosInstance.put(`/tasks/${task.id}`, {
        statut: newStatus
      });

      if (response.data.success) {
        const updatedTask = response.data.data;
        setStatus(newStatus);
        setFullTask(updatedTask);
        toast.success('Statut de la tâche mis à jour');
        if (onTaskUpdate) {
          onTaskUpdate(updatedTask);
        }
        onClose();
      } else {
        toast.error(response.data.message || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Erreur mise à jour statut:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la mise à jour du statut');
    } finally {
      setProcessing(false);
    }
  };

  const getPriorityLabel = (priorite) => {
    const labels = {
      'basse': 'Basse',
      'normale': 'Normale',
      'haute': 'Haute',
      'urgente': 'Urgente'
    };
    return labels[priorite] || priorite;
  };

  const getPriorityColor = (priorite) => {
    const colors = {
      'basse': 'bg-green-100 text-green-800',
      'normale': 'bg-blue-100 text-blue-800',
      'haute': 'bg-orange-100 text-orange-800',
      'urgente': 'bg-red-100 text-red-800'
    };
    return colors[priorite] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (statut) => {
    if (statut === 'todo') return 'À faire';
    if (statut === 'en_cours') return 'En cours';
    if (statut === 'termine') return 'Terminé';
    return statut;
  };

  const getStatusColor = (statut) => {
    if (statut === 'todo') return { bg: 'bg-blue-50', text: 'text-[#0026E2]', toggle: 'bg-[#0026E2]' };
    if (statut === 'en_cours') return { bg: 'bg-blue-50', text: 'text-[#0026E2]', toggle: 'bg-[#0026E2]' };
    if (statut === 'termine') return { bg: 'bg-green-50', text: 'text-green-600', toggle: 'bg-green-600' };
    return { bg: 'bg-gray-50', text: 'text-gray-600', toggle: 'bg-gray-600' };
  };

  // Déterminer quels toggles afficher en bas (les 2 autres que le statut actuel)
  const getAvailableToggles = () => {
    if (status === 'todo') {
      return ['en_cours', 'termine'];
    } else if (status === 'en_cours') {
      return ['todo', 'termine'];
    } else {
      return ['todo', 'en_cours'];
    }
  };

  const availableToggles = getAvailableToggles();
  const statusColor = getStatusColor(status);

  // Vérifier si l'utilisateur actuel est assigné à la tâche
  const isUserAssignedToTask = () => {
    if (!userData || !currentTask.assignments) return false;
    return currentTask.assignments.some(
      assignment => assignment.employee?.id === userData.id || assignment.employee_id === userData.id
    );
  };

  const userIsAssigned = isUserAssignedToTask();
  // Afficher les toggles si : l'utilisateur n'est pas manager OU (il est manager ET assigné à la tâche)
  const shouldShowToggles = !isManager || (isManager && userIsAssigned);

  return createPortal(
    <div 
      className="fixed inset-0 bg-black/10 backdrop-blur-sm flex items-center justify-center z-[10000] p-4" 
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#0026E2' }}></div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Titre */}
            <h2 className="text-lg font-bold text-gray-900">
              {currentTask.titre}
            </h2>

            {/* Description - Justifiée à gauche */}
            {currentTask.description && (
              <div className="text-left">
                <p className="text-sm font-semibold text-gray-700 mb-2">Description</p>
                <p className="text-sm text-gray-900 leading-relaxed">{currentTask.description}</p>
              </div>
            )}

            {/* Priorité */}
            {currentTask.priorite && (
              <div className="flex items-center gap-2">
                <p className="text-sm text-gray-600">Priorité :</p>
                <span className={`text-xs px-2 py-1 rounded-full font-semibold ${getPriorityColor(currentTask.priorite)}`}>
                  {getPriorityLabel(currentTask.priorite)}
                </span>
              </div>
            )}

            {/* Date d'échéance */}
            {currentTask.date_echeance && (
              <div className="flex items-center gap-2">
                <p className="text-sm text-gray-600">Date d'échéance :</p>
                <p className="text-sm font-medium text-gray-900">
                  {new Date(currentTask.date_echeance).toLocaleDateString('fr-FR')}
                </p>
              </div>
            )}

            {/* Assignés avec photos de profil - Style comme dans Projets.jsx */}
            {currentTask.assignments && currentTask.assignments.length > 0 && (
              <div className="flex items-center gap-2">
                <p className="text-sm text-gray-600">Assigné(s) :</p>
                <div className="flex items-center">
                  {currentTask.assignments
                    .filter(assignment => assignment != null && assignment.employee != null)
                    .slice(0, 3)
                    .map((assignment, idx) => {
                      const employeeId = assignment.employee?.id || assignment.employee_id || assignment.id || `emp-${idx}`;
                      const employeeName = assignment.employee?.nom_complet || 'Membre';
                      const profileImageUrl = assignment.employee?.profileImageUrl ? getLogoUrl(assignment.employee.profileImageUrl) : null;
                      const defaultImage = assets?.employes;
                      
                      return (
                        <div
                          key={`assignment-${assignment.id || employeeId}-${idx}`}
                          className="w-10 h-10 rounded-full overflow-hidden border-2 border-white"
                          style={{ 
                            backgroundColor: '#0026E2',
                            marginLeft: idx === 0 ? '0' : '-10px',
                            zIndex: 3 - idx
                          }}
                          title={employeeName}
                        >
                          {profileImageUrl ? (
                            <img 
                              src={profileImageUrl} 
                              alt={employeeName}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                if (e.target && defaultImage) {
                                  e.target.onerror = null;
                                  e.target.src = defaultImage;
                                }
                              }}
                            />
                          ) : defaultImage ? (
                            <img 
                              src={defaultImage} 
                              alt={employeeName}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-white text-xs font-semibold">
                              {employeeName.charAt(0).toUpperCase()}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  {currentTask.assignments.length > 3 && (
                    <div
                      className="w-10 h-10 rounded-full border-2 border-white flex items-center justify-center text-white text-xs font-semibold"
                      style={{ 
                        backgroundColor: '#0026E2',
                        marginLeft: '-10px',
                        zIndex: 0
                      }}
                      title={`${currentTask.assignments.length - 3} autre${currentTask.assignments.length - 3 > 1 ? 's' : ''}`}
                    >
                      +{currentTask.assignments.length - 3}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Séparateur */}
            <div className="border-t border-gray-200 my-4"></div>

            {/* Toggle du statut actuel - Centré et togglé par défaut */}
            <div className="flex justify-center mb-4">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${statusColor.bg}`}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    // Toggle reste sur le statut actuel (déjà activé par défaut)
                  }}
                  className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors duration-300 ${statusColor.toggle}`}
                >
                  <span className="inline-block h-4 w-4 transform rounded-full bg-white shadow transition translate-x-5" />
                </button>
                <span className={`text-sm font-medium ${statusColor.text}`}>
                  {getStatusLabel(status)}
                </span>
              </div>
            </div>

            {/* Toggles pour les employés et responsables assignés - Côte à côte */}
            {shouldShowToggles && (
              <div className="flex items-center gap-2">
                {availableToggles.map((toggleStatus) => {
                  const toggleConfig = {
                    todo: { label: 'À faire' },
                    en_cours: { label: 'En cours' },
                    termine: { label: 'Terminé' }
                  };
                  const config = toggleConfig[toggleStatus];
                  
                  return (
                    <div
                      key={toggleStatus}
                      className={`flex-1 flex items-center justify-between p-2 rounded-xl border transition-all duration-200 cursor-pointer border-gray-200 bg-white hover:border-gray-300 ${
                        processing ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      onClick={() => !processing && handleStatusChange(toggleStatus)}
                    >
                      <span className="text-xs font-medium whitespace-nowrap text-gray-900">
                        {config.label}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!processing) handleStatusChange(toggleStatus);
                        }}
                        disabled={processing}
                        className="relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-300 flex-shrink-0 bg-gray-300"
                      >
                        <span className="sr-only">Basculer {config.label}</span>
                        <span className="inline-block h-4 w-4 transform rounded-full bg-white shadow transition translate-x-0.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default TaskDetailModal;
